import { useCallback, useEffect, useState } from "react";
import {
  Copy,
  ExternalLink,
  KeyRound,
  Package,
  Plus,
  Shield,
  Trash2,
  Users,
  Mail,
  ScrollText,
  ChevronDown,
  Tent,
} from "lucide-react";
import type { Product } from "@outpost/shared";
import { AgentsPane } from "./AgentsPane";

type Member = {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: string;
  createdAt: string;
  isPlatformAdmin?: number;
};

type Invite = {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  note: string | null;
  joinUrl?: string | null;
};

type AuditEvent = {
  id: string;
  action: string;
  actorUserId: string | null;
  actorAgentTokenId: string | null;
  metaJson: string | null;
  createdAt: string;
};

type Overview = {
  organization: {
    id: string;
    name: string;
    planTier: string;
  } | null;
  counts: {
    members: number;
    pendingInvites: number;
    mcpTokens: number;
    products: number;
  };
  limits: {
    maxMembers: number;
    maxMcpTokens: number;
    maxProducts: number;
  };
};

type Platform = {
  totals: {
    organizations: number;
    users: number;
    pendingInvites: number;
  };
  organizations: Array<{
    id: string;
    name: string;
    planTier: string;
    createdAt: string;
  }>;
};

type PilotApplication = {
  id: string;
  email: string;
  productUrls: string[];
  note: string | null;
  source: string;
  createdAt: string;
};

type Props = {
  token: string;
  isPlatformAdmin: boolean;
  onOpenProduct?: (productId: string) => void;
  onAddProduct?: () => void;
};

type Tab =
  | "members"
  | "products"
  | "invites"
  | "pilot"
  | "agents"
  | "audit"
  | "platform";

async function apiGet<T>(
  path: string,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { headers, credentials: "include" });
  if (!res.ok) return { ok: false, error: await res.text() };
  return { ok: true, data: (await res.json()) as T };
}

async function apiPost<T>(
  path: string,
  token: string,
  body?: unknown,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, {
    method: "POST",
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) return { ok: false, error: await res.text() };
  return { ok: true, data: (await res.json()) as T };
}

async function apiPatch<T>(
  path: string,
  token: string,
  body?: unknown,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, {
    method: "PATCH",
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) return { ok: false, error: await res.text() };
  return { ok: true, data: (await res.json()) as T };
}

async function apiDelete(
  path: string,
  token: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, {
    method: "DELETE",
    headers,
    credentials: "include",
  });
  if (!res.ok) return { ok: false, error: await res.text() };
  return { ok: true };
}

function shortErr(raw: string): string {
  try {
    const j = JSON.parse(raw) as { message?: string; error?: string };
    return j.message || j.error || raw.slice(0, 120);
  } catch {
    return raw.slice(0, 120);
  }
}

function parseWhen(raw: string): Date {
  return new Date(raw.includes("T") ? raw : `${raw}Z`);
}

function formatJoined(raw: string): string {
  try {
    return parseWhen(raw).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return raw;
  }
}

function formatWhen(raw: string): string {
  try {
    return parseWhen(raw).toLocaleString();
  } catch {
    return raw;
  }
}

function CapacityBar({
  value,
  max,
  lit,
}: {
  value: number;
  max: number;
  lit?: boolean;
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      className={`op-ops-progress op-ops-progress-sm${lit ? " is-lit" : ""}${
        pct >= 100 ? " is-full" : ""
      }`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div className="op-ops-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function AdminPane({
  token,
  isPlatformAdmin,
  onOpenProduct,
  onAddProduct,
}: Props) {
  const [tab, setTab] = useState<Tab>("members");
  const [members, setMembers] = useState<Member[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [pilotApps, setPilotApps] = useState<PilotApplication[]>([]);
  const [pilotSlots, setPilotSlots] = useState<{
    slotsTotal: number;
    slotsTaken: number;
    slotsLeft: number;
    open: boolean;
  } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "viewer">("member");
  const [busy, setBusy] = useState(false);
  const [freshJoinUrl, setFreshJoinUrl] = useState<string | null>(null);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(
    null,
  );

  const flash = (msg: string, isErr = false) => {
    setStatus(msg);
    setErr(isErr);
    window.setTimeout(() => setStatus(null), 3200);
  };

  const refresh = useCallback(async () => {
    const [m, i, a, o, p, pilot] = await Promise.all([
      apiGet<{ members: Member[] }>("/v1/admin/members", token),
      apiGet<{ invites: Invite[] }>("/v1/invites", token),
      apiGet<{ events: AuditEvent[] }>("/v1/admin/audit?limit=40", token),
      apiGet<Overview>("/v1/admin/overview", token),
      apiGet<{ products: Product[] }>("/v1/products", token),
      apiGet<{
        applications: PilotApplication[];
        slotsTotal: number;
        slotsTaken: number;
        slotsLeft: number;
        open: boolean;
      }>("/v1/pilot/applications", token),
    ]);
    if (m.ok) setMembers(m.data.members);
    if (i.ok) setInvites(i.data.invites);
    if (a.ok) setAudit(a.data.events);
    if (o.ok) setOverview(o.data);
    if (p.ok) setProducts(p.data.products);
    if (pilot.ok) {
      setPilotApps(pilot.data.applications);
      setPilotSlots({
        slotsTotal: pilot.data.slotsTotal,
        slotsTaken: pilot.data.slotsTaken,
        slotsLeft: pilot.data.slotsLeft,
        open: pilot.data.open,
      });
    }
    if (isPlatformAdmin) {
      const plat = await apiGet<Platform>("/v1/admin/platform", token);
      if (plat.ok) setPlatform(plat.data);
    }
  }, [token, isPlatformAdmin]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function createInvite() {
    const email = inviteEmail.trim();
    if (!email) {
      flash("Email required", true);
      return;
    }
    setBusy(true);
    const res = await apiPost<{
      invite: Invite;
      rawToken: string;
      emailed: boolean;
      emailMessage: string | null;
    }>("/v1/invites", token, {
      email,
      role: inviteRole,
      sendEmail: true,
    });
    setBusy(false);
    if (!res.ok) {
      flash(shortErr(res.error), true);
      return;
    }
    const join =
      res.data.invite.joinUrl ||
      `${window.location.origin}/join?invite=${encodeURIComponent(res.data.rawToken)}`;
    setFreshJoinUrl(join);
    setInviteEmail("");
    flash(
      res.data.emailed
        ? `Invite sent to ${email}`
        : `Invite created — email skipped: ${res.data.emailMessage ?? "copy join link"}`,
    );
    await refresh();
  }

  async function revokeInvite(id: string) {
    const res = await apiDelete(`/v1/invites/${id}`, token);
    if (!res.ok) flash(shortErr(res.error), true);
    else {
      flash("Invite revoked");
      await refresh();
    }
  }

  async function changeRole(userId: string, role: "member" | "viewer") {
    const res = await apiPatch(`/v1/admin/members/${userId}`, token, { role });
    if (!res.ok) flash(shortErr(res.error), true);
    else {
      flash("Role updated");
      await refresh();
    }
  }

  async function removeMember(userId: string) {
    if (!window.confirm("Remove this member from the org?")) return;
    const res = await apiDelete(`/v1/admin/members/${userId}`, token);
    if (!res.ok) flash(shortErr(res.error), true);
    else {
      flash("Member removed");
      setExpandedMemberId(null);
      await refresh();
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      flash("Copied");
    } catch {
      flash("Copy failed", true);
    }
  }

  const tabs: { id: Tab; label: string; Icon: typeof Users }[] = [
    { id: "members", label: "Members", Icon: Users },
    { id: "products", label: "Products", Icon: Package },
    { id: "invites", label: "Invites", Icon: Mail },
    { id: "pilot", label: "Pilot", Icon: Tent },
    { id: "agents", label: "Agents / MCP", Icon: KeyRound },
    { id: "audit", label: "Audit", Icon: ScrollText },
  ];
  if (isPlatformAdmin) {
    tabs.push({ id: "platform", label: "Platform", Icon: Shield });
  }

  const counts = overview?.counts;
  const limits = overview?.limits;
  const pendingInvites =
    invites.filter((inv) => !inv.acceptedAt && !inv.revokedAt).length ||
    counts?.pendingInvites ||
    0;

  return (
    <div className="op-admin" aria-label="Admin">
      <div className="op-ops-stage op-admin-stage">
        <div className="op-ops-embers" aria-hidden />
        <div className="op-ops-heading">
          <div>
            <h2>{overview?.organization?.name ?? "Organization"}</h2>
            <small>
              Workspace admin
              {overview?.organization?.planTier
                ? ` · ${overview.organization.planTier}`
                : ""}{" "}
              — members, products, and access for this org only.
            </small>
          </div>
          <span className="op-tag">Org control</span>
        </div>

        {counts && limits ? (
          <div className="op-ops-hero-metrics op-admin-metrics" aria-live="polite">
            <button
              type="button"
              className={`op-ops-metric op-admin-metric${
                tab === "members" ? " is-active" : ""
              }`}
              onClick={() => setTab("members")}
            >
              <strong>
                {counts.members}
                <span className="op-admin-metric-max">/{limits.maxMembers}</span>
              </strong>
              <span>Members</span>
              <CapacityBar
                value={counts.members}
                max={limits.maxMembers}
                lit={counts.members > 0}
              />
            </button>
            <button
              type="button"
              className={`op-ops-metric op-admin-metric${
                tab === "products" ? " is-active" : ""
              }`}
              onClick={() => setTab("products")}
            >
              <strong>
                {counts.products}
                <span className="op-admin-metric-max">/{limits.maxProducts}</span>
              </strong>
              <span>Products</span>
              <CapacityBar
                value={counts.products}
                max={limits.maxProducts}
                lit={counts.products > 0}
              />
            </button>
            <button
              type="button"
              className={`op-ops-metric op-admin-metric${
                tab === "invites" ? " is-active" : ""
              }${pendingInvites > 0 ? " op-ops-metric-live is-lit" : ""}`}
              onClick={() => setTab("invites")}
            >
              <strong>{pendingInvites}</strong>
              <span>Pending invites</span>
            </button>
            <button
              type="button"
              className={`op-ops-metric op-admin-metric${
                tab === "pilot" ? " is-active" : ""
              }${(pilotSlots?.slotsTaken ?? 0) > 0 ? " op-ops-metric-live is-lit" : ""}`}
              onClick={() => setTab("pilot")}
            >
              <strong>
                {pilotSlots?.slotsTaken ?? 0}
                <span className="op-admin-metric-max">
                  /{pilotSlots?.slotsTotal ?? 5}
                </span>
              </strong>
              <span>Pilot slots</span>
              <CapacityBar
                value={pilotSlots?.slotsTaken ?? 0}
                max={pilotSlots?.slotsTotal ?? 5}
                lit={(pilotSlots?.slotsTaken ?? 0) > 0}
              />
            </button>
            <button
              type="button"
              className={`op-ops-metric op-admin-metric${
                tab === "agents" ? " is-active" : ""
              }`}
              onClick={() => setTab("agents")}
            >
              <strong>
                {counts.mcpTokens}
                <span className="op-admin-metric-max">
                  /{limits.maxMcpTokens}
                </span>
              </strong>
              <span>MCP tokens</span>
              <CapacityBar
                value={counts.mcpTokens}
                max={limits.maxMcpTokens}
                lit={counts.mcpTokens > 0}
              />
            </button>
          </div>
        ) : (
          <p className="op-ops-hint">Loading workspace capacity…</p>
        )}
      </div>

      {status ? (
        <p className={`op-notice ${err ? "err" : ""}`} role="status">
          {status}
        </p>
      ) : null}

      <div className="op-ops-filters op-admin-tabs" role="tablist">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            aria-pressed={tab === id}
            className={tab === id ? "is-active" : undefined}
            onClick={() => setTab(id)}
          >
            <Icon size={14} strokeWidth={2} aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {tab === "members" ? (
        <section className="op-admin-section" aria-label="Members">
          {members.length === 0 ? (
            <p className="op-admin-empty">
              No members on this workspace yet — invite someone when you’re
              ready.
            </p>
          ) : (
            <ul className="op-admin-gallery">
              {members.map((m, i) => {
                const open = expandedMemberId === m.id;
                return (
                  <li
                    key={m.id}
                    className={`op-admin-tile${open ? " is-open" : ""}`}
                    style={{ ["--op-admin-i" as string]: i }}
                  >
                    <button
                      type="button"
                      className="op-admin-tile-toggle"
                      aria-expanded={open}
                      onClick={() =>
                        setExpandedMemberId(open ? null : m.id)
                      }
                    >
                      <div className="op-admin-tile-main">
                        <span className="op-admin-avatar" aria-hidden>
                          {(m.name || m.email).slice(0, 1).toUpperCase()}
                        </span>
                        <div>
                          <strong>{m.name || m.email}</strong>
                          <small>{m.email}</small>
                        </div>
                      </div>
                      <div className="op-admin-tile-meta">
                        <span className={`op-admin-role is-${m.role}`}>
                          {m.role}
                        </span>
                        {m.isPlatformAdmin ? (
                          <span className="op-admin-role is-platform">
                            platform
                          </span>
                        ) : null}
                        <span className="op-admin-joined">
                          Joined {formatJoined(m.createdAt)}
                        </span>
                        <ChevronDown
                          size={14}
                          className="op-admin-chevron"
                          aria-hidden
                        />
                      </div>
                    </button>
                    {open ? (
                      <div className="op-admin-tile-detail">
                        <dl className="op-admin-dl">
                          <div>
                            <dt>User id</dt>
                            <dd>
                              <code>{m.userId}</code>
                            </dd>
                          </div>
                          <div>
                            <dt>Membership</dt>
                            <dd>{formatWhen(m.createdAt)}</dd>
                          </div>
                          <div>
                            <dt>Role</dt>
                            <dd>{m.role}</dd>
                          </div>
                        </dl>
                        {m.role !== "owner" ? (
                          <div className="op-admin-actions">
                            <label className="op-gate-label">
                              Change role
                              <select
                                className="op-field"
                                value={m.role}
                                onChange={(e) =>
                                  void changeRole(
                                    m.userId,
                                    e.target.value as "member" | "viewer",
                                  )
                                }
                              >
                                <option value="member">member</option>
                                <option value="viewer">viewer</option>
                              </select>
                            </label>
                            <button
                              type="button"
                              className="op-ghost"
                              aria-label="Remove member"
                              onClick={() => void removeMember(m.userId)}
                            >
                              <Trash2 size={14} /> Remove
                            </button>
                          </div>
                        ) : (
                          <p className="op-muted">
                            Owner role is fixed for this workspace.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {tab === "products" ? (
        <section className="op-admin-section" aria-label="Products">
          <div className="op-admin-section-head">
            <p className="op-muted">
              Registered product spaces in this org
              {limits ? ` · ${products.length}/${limits.maxProducts}` : ""}.
            </p>
            {onAddProduct ? (
              <button
                type="button"
                className="op-primary"
                onClick={onAddProduct}
              >
                <Plus size={14} aria-hidden /> Add product
              </button>
            ) : null}
          </div>
          {products.length === 0 ? (
            <p className="op-admin-empty">
              No products registered yet — add a space when you’re ready to
              monitor it.
            </p>
          ) : (
            <ul className="op-admin-gallery">
              {products.map((p, i) => {
                const open = expandedProductId === p.id;
                return (
                  <li
                    key={p.id}
                    className={`op-admin-tile${open ? " is-open" : ""}`}
                    style={{ ["--op-admin-i" as string]: i }}
                  >
                    <button
                      type="button"
                      className="op-admin-tile-toggle"
                      aria-expanded={open}
                      onClick={() =>
                        setExpandedProductId(open ? null : p.id)
                      }
                    >
                      <div className="op-admin-tile-main">
                        <span
                          className="op-admin-avatar op-admin-avatar-product"
                          aria-hidden
                        >
                          <Package size={16} strokeWidth={2} />
                        </span>
                        <div>
                          <strong>{p.name}</strong>
                          <small>/{p.slug}</small>
                        </div>
                      </div>
                      <div className="op-admin-tile-meta">
                        {p.url ? (
                          <span className="op-admin-joined op-admin-url">
                            {p.url.replace(/^https?:\/\//, "")}
                          </span>
                        ) : (
                          <span className="op-admin-joined">No URL yet</span>
                        )}
                        <span className="op-admin-joined">
                          Added {formatJoined(p.createdAt)}
                        </span>
                        <ChevronDown
                          size={14}
                          className="op-admin-chevron"
                          aria-hidden
                        />
                      </div>
                    </button>
                    {open ? (
                      <div className="op-admin-tile-detail">
                        <dl className="op-admin-dl">
                          <div>
                            <dt>Product id</dt>
                            <dd>
                              <code>{p.id}</code>
                            </dd>
                          </div>
                          <div>
                            <dt>Slug</dt>
                            <dd>{p.slug}</dd>
                          </div>
                          {p.oneLiner ? (
                            <div>
                              <dt>One-liner</dt>
                              <dd>{p.oneLiner}</dd>
                            </div>
                          ) : null}
                          {p.url ? (
                            <div>
                              <dt>URL</dt>
                              <dd>
                                <a
                                  href={p.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="op-admin-ext"
                                >
                                  {p.url} <ExternalLink size={12} aria-hidden />
                                </a>
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                        <div className="op-admin-actions">
                          {onOpenProduct ? (
                            <button
                              type="button"
                              className="op-primary"
                              onClick={() => onOpenProduct(p.id)}
                            >
                              Open settings
                            </button>
                          ) : null}
                          {p.url ? (
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="op-ghost op-admin-ext-btn"
                            >
                              <ExternalLink size={14} aria-hidden /> Visit
                            </a>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {tab === "invites" ? (
        <section className="op-admin-section" aria-label="Invites">
          <div className="op-admin-create op-admin-create-panel">
            <label className="op-gate-label">
              Email
              <input
                className="op-field"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="founder@example.com"
              />
            </label>
            <label className="op-gate-label">
              Role
              <select
                className="op-field"
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(e.target.value as "member" | "viewer")
                }
              >
                <option value="member">member</option>
                <option value="viewer">viewer</option>
              </select>
            </label>
            <button
              type="button"
              className="op-primary"
              disabled={busy}
              onClick={() => void createInvite()}
            >
              <Plus size={14} aria-hidden /> Create invite
            </button>
          </div>
          {freshJoinUrl ? (
            <p className="op-admin-join">
              Join link:{" "}
              <button
                type="button"
                className="op-linkish"
                onClick={() => void copy(freshJoinUrl)}
              >
                <Copy size={12} aria-hidden /> {freshJoinUrl}
              </button>
            </p>
          ) : null}
          {invites.length === 0 ? (
            <p className="op-admin-empty">
              No invites yet — create one above when you want someone in.
            </p>
          ) : (
            <ul className="op-admin-gallery">
              {invites.map((inv, i) => {
                const closed = Boolean(inv.acceptedAt || inv.revokedAt);
                const state = inv.acceptedAt
                  ? "accepted"
                  : inv.revokedAt
                    ? "revoked"
                    : "pending";
                return (
                  <li
                    key={inv.id}
                    className="op-admin-tile is-compact"
                    style={{ ["--op-admin-i" as string]: i }}
                  >
                    <div className="op-admin-tile-toggle is-static">
                      <div className="op-admin-tile-main">
                        <span className="op-admin-avatar" aria-hidden>
                          <Mail size={14} />
                        </span>
                        <div>
                          <strong>
                            {inv.email === "*"
                              ? "Open share link"
                              : inv.email}
                          </strong>
                          <small>
                            Expires {formatJoined(inv.expiresAt)}
                          </small>
                        </div>
                      </div>
                      <div className="op-admin-tile-meta">
                        <span className={`op-admin-role is-${inv.role}`}>
                          {inv.role}
                        </span>
                        <span className={`op-admin-role is-${state}`}>
                          {state}
                        </span>
                        {!closed ? (
                          <div className="op-admin-actions">
                            {inv.joinUrl ? (
                              <button
                                type="button"
                                className="op-ghost"
                                aria-label="Copy join link"
                                onClick={() => void copy(inv.joinUrl!)}
                              >
                                <Copy size={14} />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="op-ghost"
                              aria-label="Revoke invite"
                              onClick={() => void revokeInvite(inv.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {tab === "pilot" ? (
        <section className="op-admin-section" aria-label="Pilot applications">
          <p className="op-ops-hint">
            Feedback pilot from the public landing.{" "}
            {pilotSlots
              ? pilotSlots.open
                ? `${pilotSlots.slotsLeft} of ${pilotSlots.slotsTotal} slots left.`
                : "All slots filled."
              : "Loading capacity…"}{" "}
            <a href="/#pilot" target="_blank" rel="noreferrer">
              Open landing form
            </a>
          </p>
          {pilotApps.length === 0 ? (
            <p className="op-ops-hint">No pilot applications yet.</p>
          ) : (
            <ul className="op-admin-list">
              {pilotApps.map((app) => (
                <li key={app.id} className="op-admin-row op-admin-pilot-row">
                  <div className="op-admin-row-main">
                    <strong>
                      <a href={`mailto:${app.email}`}>{app.email}</a>
                    </strong>
                    <span className="op-muted">
                      {formatWhen(app.createdAt)} · {app.source}
                    </span>
                  </div>
                  {app.productUrls.length > 0 ? (
                    <ul className="op-admin-pilot-urls">
                      {app.productUrls.map((u) => (
                        <li key={u}>
                          <a href={u} target="_blank" rel="noreferrer">
                            {u}
                            <ExternalLink size={11} aria-hidden />
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="op-ops-hint">No product URLs</p>
                  )}
                  {app.note ? (
                    <p className="op-admin-pilot-note">{app.note}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {tab === "agents" ? <AgentsPane token={token} /> : null}

      {tab === "audit" ? (
        <section className="op-admin-section" aria-label="Audit">
          {audit.length === 0 ? (
            <p className="op-admin-empty">
              Quiet so far — audit events will show here as the workspace
              changes.
            </p>
          ) : (
            <ul className="op-admin-gallery">
              {audit.map((ev, i) => (
                <li
                  key={ev.id}
                  className="op-admin-tile is-compact"
                  style={{ ["--op-admin-i" as string]: i }}
                >
                  <div className="op-admin-tile-toggle is-static">
                    <div className="op-admin-tile-main">
                      <div>
                        <strong>{ev.action}</strong>
                        <small>
                          {ev.actorUserId ||
                            ev.actorAgentTokenId ||
                            "system"}
                        </small>
                      </div>
                    </div>
                    <div className="op-admin-tile-meta">
                      <span className="op-admin-joined">
                        {formatWhen(ev.createdAt)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {tab === "platform" && platform ? (
        <section className="op-admin-section" aria-label="Platform">
          <div className="op-ops-hero-metrics op-admin-metrics">
            <div className="op-ops-metric">
              <strong>{platform.totals.organizations}</strong>
              <span>Orgs</span>
            </div>
            <div className="op-ops-metric">
              <strong>{platform.totals.users}</strong>
              <span>Users</span>
            </div>
            <div
              className={`op-ops-metric${
                platform.totals.pendingInvites > 0
                  ? " op-ops-metric-live is-lit"
                  : ""
              }`}
            >
              <strong>{platform.totals.pendingInvites}</strong>
              <span>Pending invites</span>
            </div>
          </div>
          <p className="op-muted">
            Outpost16-wide view — only visible to platform admins.
          </p>
          <ul className="op-admin-gallery">
            {platform.organizations.map((o, i) => (
              <li
                key={o.id}
                className="op-admin-tile is-compact"
                style={{ ["--op-admin-i" as string]: i }}
              >
                <div className="op-admin-tile-toggle is-static">
                  <div className="op-admin-tile-main">
                    <div>
                      <strong>{o.name}</strong>
                      <small>
                        <code>{o.id}</code>
                      </small>
                    </div>
                  </div>
                  <div className="op-admin-tile-meta">
                    <span className="op-admin-role is-member">{o.planTier}</span>
                    <span className="op-admin-joined">
                      {formatJoined(o.createdAt)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
